/**
 * InvoicingDashboard — the Monthly Invoicing Tracker.
 *
 * Standalone dashboard (gated by invoicing.view) for the Ad Sales team and the
 * accountant. Shows the selected month's invoices with live GAM pacing and an
 * over-delivery guard, and supports an explicit Edit mode where everything is
 * overridable. Edits flow into per-row drafts and save individually (or all at
 * once) — billing fields (Billable Total + Status) require invoicing.billing.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardFrame, KpiCard, Toast, ToastKind, LoadingDots, EmptyState } from '../ui';
import FilterChipGroup from '../Sales/FilterChipGroup';
import InvoiceRow from './InvoiceRow';
import ImportSheetModal from './ImportSheetModal';
import {
  getInvoices,
  getInvoicingStatus,
  updateInvoice,
  markInvoiceReviewed,
  resyncPacing,
  autoLinkGam,
  backfillIo,
  generateMonthlyInvoice,
  downloadMonthlyInvoice,
  refreshMonthlyDelivered,
  dedupeInvoices,
  acceptGamLink,
} from '../../services/invoicingApi';
import {
  Invoice,
  InvoicePatch,
  InvoicingStatus,
  CampaignSummary,
  GamAutoLinkSummary,
  GamLinkSuggestion,
  isConfirmed,
} from './types';
import { gamDataReady } from './gamSync';
import { isUnpaidStatus } from './statusText';
import './Invoicing.css';

/**
 * Build a per-invoice "why not synced" map from an auto-link summary: the
 * unmatched reasons, plus any found-but-not-auto-applied candidates (so a row
 * the linker matched only at medium confidence shows "possible match — confirm").
 */
function buildLinkDiagnostics(summary: GamAutoLinkSummary): Record<string, string> {
  const map: Record<string, string> = {};
  for (const d of summary.unmatchedDetails ?? []) map[d.invoiceId] = d.reason;
  for (const s of summary.suggestions ?? []) {
    if (!s.applied && !map[s.invoiceId]) map[s.invoiceId] = `Possible GAM match — ${s.reason}`;
  }
  return map;
}

/**
 * Suggestions a human can confirm with ONE click: not applied, a known order id,
 * and exactly one matched order (multi-order matches stay text-only — picking
 * between orders needs the edit form, not a single button).
 */
function buildSuggestionMap(summary: GamAutoLinkSummary): Record<string, GamLinkSuggestion> {
  const map: Record<string, GamLinkSuggestion> = {};
  for (const s of summary.suggestions ?? []) {
    if (!s.applied && s.gamOrderId && (s.matchedOrders?.length ?? 0) === 1 && !map[s.invoiceId]) {
      map[s.invoiceId] = s;
    }
  }
  return map;
}

/** Format a dollar amount with cents (billing figures must not drop cents). */
const fmtUsd = (n: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type FilterKey = 'all' | 'active' | 'needs_review' | 'over_risk' | 'unpaid';

const FILTERS: { value: FilterKey; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'over_risk', label: 'Over-delivery risk' },
  { value: 'unpaid', label: 'Unpaid' },
];

// Single source of truth for the table columns, in sheet order. COL_COUNT (used
// for the group-header / detail colSpan) is derived from this so it can never
// drift from the rendered header count. NOTE: InvoiceRow's main-row <td>s must be
// kept in this same order.
const COLUMNS: Array<{ label: string; align?: 'right' }> = [
  { label: '' }, // expand chevron
  { label: 'Advertiser' },
  { label: 'Order ID' },
  { label: 'IO' },
  { label: 'Monthly Flight' },
  { label: 'Total Campaign Flight' },
  { label: 'Rate ($)', align: 'right' },
  { label: 'Planned (impressions)', align: 'right' },
  { label: '1P Delivered (GAM360, YT / GA)', align: 'right' },
  { label: '3P Delivered (DV, IAS)', align: 'right' },
  { label: 'Pacing (month)' },
  { label: 'Planned Total ($)', align: 'right' },
  { label: 'Billable Total($)', align: 'right' },
  { label: 'Status' },
];
const COL_COUNT = COLUMNS.length;
const AUTO_REFRESH_MS = 5 * 60 * 1000;
// While rows are in a "Syncing GAM…" state, poll the month faster than the
// idle auto-refresh so figures appear as each detached report completes.
const GAM_POLL_MS = 20 * 1000;
// How long a kicked sync keeps fast-polling alive without the server reporting
// an in-flight report (covers auto-link → refresh start gaps and queued jobs).
const GAM_POLL_GRACE_MS = 8 * 60 * 1000;

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Active = synced from GAM and not past its flight end. `>= 0` keeps a campaign
// on its final delivery day (daysRemaining === 0) — exactly when over-delivery
// risk peaks — in the Active filter/KPI.
const isActive = (i: Invoice) => !!i.delivery.lastPacingSyncAt && (i.delivery.daysRemaining ?? 0) >= 0;
// Only count risk the UI is willing to SHOW: while a linked 1P row awaits its
// month-true report (gamDataReady false), overDeliveryFlag was computed from the
// LIFETIME numerator — counting it here creates a phantom "Action needed" KPI
// pointing at a row whose pacing cell says "Syncing GAM…" with no numbers.
const isOverRisk = (i: Invoice) =>
  gamDataReady(i) &&
  (i.delivery.overDeliveryFlag === 'exceeded' || i.delivery.overDeliveryFlag === 'approaching');
// Same negation trap as isConfirmed: "Unpaid"/"Not paid" contain the substring
// "paid" and must still count as unpaid. Parsing shared with the Reconciliation
// dashboard's Paid cell (statusText.ts).
const isUnpaid = (i: Invoice) => isUnpaidStatus(i.status || '');

interface CampaignGroup {
  campaignKey: string;
  items: Invoice[];
  advertiser: string;
  orderId?: string;
  /** Whole-campaign billable (all months), not just the viewed month. */
  totalBillable: number;
  /** Total months in the campaign (may exceed the rows shown for this month). */
  totalMonths: number;
  /** True when the campaign spans months outside the one currently in view. */
  spansOtherMonths: boolean;
  /** Whole-campaign confirmation state. */
  allConfirmed: boolean;
  /** A CONFIRMED month is missing its Billable Total (would under-bill). */
  hasUnpriced: boolean;
}

const InvoicingDashboard: React.FC = () => {
  const [status, setStatus] = useState<InvoicingStatus>({ canView: true, canManage: false, canBill: false });
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [campaignSummaries, setCampaignSummaries] = useState<Record<string, CampaignSummary>>({});
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthKey());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, InvoicePatch>>({});
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [generatingKey, setGeneratingKey] = useState<string | null>(null);
  // Invoice ids with a delivered-refresh in flight (disables the row's button).
  const [refreshingDelivered, setRefreshingDelivered] = useState<Set<string>>(new Set());

  const dirtyIds = useMemo(
    () => Object.keys(drafts).filter((id) => drafts[id] && Object.keys(drafts[id]).length > 0),
    [drafts],
  );

  // Monotonic request id: loaders overlap freely (month switch + 20s fast-poll +
  // 5-min idle refresh + post-action reloads), and without this guard a SLOW
  // response for the previous month lands last, snapping the table AND the month
  // selector back to a month the user already left — or overwriting a fresher
  // quick-patch merge with a pre-patch snapshot.
  const loadSeq = useRef(0);
  const loadInvoices = useCallback(async (month: string) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getInvoices({ month });
      if (seq !== loadSeq.current) return; // a newer load superseded this one
      setInvoices(res.invoices);
      setCampaignSummaries(res.campaignSummaries ?? {});
      setAvailableMonths(res.availableMonths);
      setSelectedMonth(res.monthKey);
      setLastFetchedAt(res.lastFetchedAt);
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      setError(err?.message || 'Failed to load invoices.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  // Auto-link newly-imported invoices to GAM and refresh delivery/pacing for the
  // displayed month, then reload so the table reflects fresh GAM data. Runs on
  // initial load and on every month change. Gated on canManage (the POST
  // endpoints require invoicing.manage); view-only users keep the last-synced
  // pacing. Failures are non-fatal — cached data is already on screen.
  // Mirror the selected month into a ref so an in-flight sync can tell whether the
  // user has since switched months — prevents a late-completing sync from snapping
  // the view back to a stale month.
  // Per-invoice "why not synced" reasons from the last auto-link, keyed by id.
  const [linkDiagnostics, setLinkDiagnostics] = useState<Record<string, string>>({});
  // One-click-confirmable fuzzy matches from the last auto-link, keyed by id
  // (kept in lockstep with linkDiagnostics — set/reset at the same call sites).
  const [gamSuggestions, setGamSuggestions] = useState<Record<string, GamLinkSuggestion>>({});

  const selectedMonthRef = useRef(selectedMonth);
  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  // Re-entry guard: rapid month switching must not stack concurrent
  // auto-link + pacing syncs for the same month.
  const syncInFlightMonth = useRef<string | null>(null);

  // Keeps the GAM fast-poll loop (effect below) alive for a grace window after
  // we kick a sync ourselves, before the server reports a report in flight.
  const pollUntilRef = useRef(0);
  const kickGamPolling = useCallback(() => {
    pollUntilRef.current = Date.now() + GAM_POLL_GRACE_MS;
  }, []);

  const autoSyncMonth = useCallback(
    async (month: string) => {
      if (!status.canManage) return;
      if (syncInFlightMonth.current === month) return;
      syncInFlightMonth.current = month;
      setAutoLinking(true);
      try {
        // Link newly-imported invoices WITHOUT a chained pacing sync; the explicit
        // resyncPacing below covers both the freshly-linked and already-linked rows
        // in one pass (avoids syncing GAM twice).
        const { summary } = await autoLinkGam({ month, apply: true, thenSyncPacing: false });
        // Month-guarded: a slow sync for the PREVIOUS month must not overwrite
        // the chips/diagnostics the newly-selected month's sync just set.
        if (selectedMonthRef.current === month) {
          setLinkDiagnostics(buildLinkDiagnostics(summary));
          setGamSuggestions(buildSuggestionMap(summary));
        }
        await resyncPacing({ month });
      } catch {
        /* non-fatal: the dashboard already shows cached Firestore data */
      } finally {
        if (syncInFlightMonth.current === month) syncInFlightMonth.current = null;
        // Same guard: don't clear the header "Syncing…" chip while the sync for
        // the month the user is LOOKING AT is still running.
        if (selectedMonthRef.current === month) setAutoLinking(false);
        // Fresh links wipe month-true caches and the backend kicks detached
        // refreshes — keep the fast-poll alive so the figures stream in.
        kickGamPolling();
        // Only refresh the view if the user hasn't navigated to another month.
        if (selectedMonthRef.current === month) await loadInvoices(month);
      }
    },
    [status.canManage, loadInvoices, kickGamPolling],
  );

  useEffect(() => {
    getInvoicingStatus().then(setStatus).catch(() => {});
    loadInvoices(currentMonthKey());
  }, [loadInvoices]);

  // Once we know the user can manage, auto-link + pace the current month on first
  // load (status resolves async, so this can't live in the mount effect above).
  const didInitialSync = useRef(false);
  useEffect(() => {
    if (status.canManage && !didInitialSync.current) {
      didInitialSync.current = true;
      autoSyncMonth(currentMonthKey());
    }
  }, [status.canManage, autoSyncMonth]);

  // Auto-refresh the current month only, paused while editing / dirty / hidden.
  useEffect(() => {
    const tick = () => {
      if (editMode || dirtyIds.length > 0) return;
      if (selectedMonth !== currentMonthKey()) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      loadInvoices(selectedMonth);
    };
    const id = window.setInterval(tick, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [editMode, dirtyIds.length, selectedMonth, loadInvoices]);

  // Fast-poll while GAM month-true reports are pending, so "Syncing GAM…" rows
  // fill in as each detached report completes. Driven by the server-decorated
  // monthRefreshInFlight flag, plus a grace window after we kick a sync
  // ourselves (the detached refresh may not have registered yet). Any month —
  // unlike the idle refresher, billing is often for a past month.
  useEffect(() => {
    const tick = () => {
      if (editMode || dirtyIds.length > 0) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      // A row re-refreshing an EXISTING month figure is gamDataReady (the old
      // figure is still shown) but its report is in flight — it must keep the
      // poll alive or "Refresh delivered" on a past month never streams its
      // result in (the first tick used to cancel the grace window here).
      const inFlight = invoices.some((inv) => inv.monthRefreshInFlight);
      const pending = invoices.some((inv) => !gamDataReady(inv)) || inFlight;
      if (!pending) {
        pollUntilRef.current = 0;
        return;
      }
      if (!inFlight && Date.now() > pollUntilRef.current) return;
      loadInvoices(selectedMonth);
    };
    const id = window.setInterval(tick, GAM_POLL_MS);
    return () => window.clearInterval(id);
  }, [editMode, dirtyIds.length, invoices, selectedMonth, loadInvoices]);

  // Toast with kind-aware duration: errors/warnings linger (and are dismissable),
  // successes get out of the way. The timer ref prevents an older flash's timeout
  // from hiding a newer message early.
  const toastTimer = useRef<number | null>(null);
  const flash = useCallback((kind: ToastKind, title: string, message?: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ kind, title, message });
    const duration = kind === 'error' || kind === 'warning' ? 8000 : 3500;
    toastTimer.current = window.setTimeout(() => setToast(null), duration);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const monthOptions = useMemo(() => {
    const set = new Set([currentMonthKey(), selectedMonth, ...availableMonths]);
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [availableMonths, selectedMonth]);

  const filtered = useMemo(() => {
    let result = invoices;
    if (filter === 'active') result = result.filter(isActive);
    else if (filter === 'needs_review') result = result.filter((i) => i.needsReview);
    else if (filter === 'over_risk') result = result.filter(isOverRisk);
    else if (filter === 'unpaid') result = result.filter(isUnpaid);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (i) =>
          i.advertiser.toLowerCase().includes(q) ||
          (i.campaignOrderId || '').toLowerCase().includes(q) ||
          (i.agency || '').toLowerCase().includes(q) ||
          (i.description || '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [invoices, filter, search]);

  // Group the filtered rows by campaign so the table can show a campaign header
  // (with the Generate Invoice action) above each campaign's monthly flights.
  const campaignGroups = useMemo<CampaignGroup[]>(() => {
    const map = new Map<string, Invoice[]>();
    for (const inv of filtered) {
      const key = inv.campaignKey || inv.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(inv);
    }
    return Array.from(map.entries()).map(([campaignKey, items]) => {
      // Prefer the backend's whole-campaign rollup; fall back to month-scoped
      // figures only if the summary is unavailable.
      const summary = campaignSummaries[campaignKey];
      const monthTotal = items.reduce((s, i) => s + (i.billableTotal ?? 0), 0);
      const monthAllConfirmed = items.every((i) => isConfirmed(i.status));
      const monthHasUnpriced = items.some((i) => isConfirmed(i.status) && i.billableTotal == null);
      const allConfirmed = summary ? summary.allConfirmed : monthAllConfirmed;
      const hasUnpriced = summary ? summary.hasUnpriced : monthHasUnpriced;
      const totalMonths = summary ? summary.totalMonths : items.length;
      return {
        campaignKey,
        items,
        advertiser: items.find((i) => i.advertiser)?.advertiser || '—',
        orderId: items.find((i) => i.campaignOrderId)?.campaignOrderId,
        totalBillable: summary ? summary.totalBillable : monthTotal,
        totalMonths,
        spansOtherMonths: totalMonths > items.length,
        allConfirmed,
        hasUnpriced,
      };
    });
  }, [filtered, campaignSummaries]);

  const kpis = useMemo(() => {
    return {
      active: invoices.filter(isActive).length,
      planned: invoices.reduce((s, i) => s + (i.plannedTotal ?? 0), 0),
      billable: invoices.reduce((s, i) => s + (i.billableTotal ?? 0), 0),
      needsReview: invoices.filter((i) => i.needsReview).length,
      overRisk: invoices.filter(isOverRisk).length,
    };
  }, [invoices]);

  // ----- edit handlers -----

  const onDraftChange = (id: string, field: keyof InvoicePatch, value: unknown) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  // `monthRefreshInFlight` is a GET /invoices decoration — PATCH/review responses
  // return the bare invoice, so a naive replace would flip a "Syncing GAM…" row
  // to "Awaiting" mid-report and could stall the fast-poll. Preserve the local flag.
  const mergeInvoice = (updated: Invoice) =>
    setInvoices((prev) =>
      prev.map((i) =>
        i.id === updated.id ? { ...updated, monthRefreshInFlight: i.monthRefreshInFlight } : i,
      ),
    );

  const saveRow = async (id: string) => {
    const patch = drafts[id];
    if (!patch || Object.keys(patch).length === 0) return;
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      const { invoice } = await updateInvoice(id, patch);
      mergeInvoice(invoice);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      flash('success', 'Saved.');
    } catch (err: any) {
      flash('error', 'Save failed', err?.message);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const saveAll = async () => {
    for (const id of dirtyIds) {
      // eslint-disable-next-line no-await-in-loop
      await saveRow(id);
    }
  };

  // Immediate single-field PATCH for the in-table quick controls (3P toggle,
  // inline 3P entry, one-click CONFIRM) — no draft. For Firestore-only
  // recomputes (3P rows) the backend resyncs pacing in the same request, so the
  // returned invoice already carries the fresh snapshot; a 3P→1P flip resyncs
  // GAM detached, so we pull the month again once that lands. Resolves false on
  // failure so callers (the inline 3P input) can revert local state.
  const onQuickPatch = async (id: string, patch: InvoicePatch): Promise<boolean> => {
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      const { invoice } = await updateInvoice(id, patch);
      mergeInvoice(invoice);
      if (patch.deliverySourceOfTruth === '3P') {
        flash('success', '3P verification on', 'GAM is no longer fetched for this IO — enter the agency-confirmed number in the 3P column.');
      } else if (patch.deliverySourceOfTruth === '1P') {
        flash('success', 'Back to GAM (1P)', 'Pacing is resyncing from GAM in the background — this row refreshes shortly.');
        const month = selectedMonthRef.current;
        window.setTimeout(() => {
          if (selectedMonthRef.current === month) loadInvoices(month);
        }, 7000);
      } else if (patch.status !== undefined) {
        if (isConfirmed(patch.status)) {
          flash(
            'success',
            `${invoice.monthKey} marked CONFIRMED`,
            invoice.billableTotal == null
              ? 'Set this month’s Billable Total to unlock invoice generation.'
              : 'Invoice generation is now unlocked for this month.',
          );
        } else {
          flash('success', 'Status updated.');
        }
      } else {
        flash('success', '3P delivered saved.');
      }
      return true;
    } catch (err: any) {
      flash('error', 'Save failed', err?.message);
      return false;
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // One-click accept of a fuzzy GAM suggestion. The backend re-fetches the
  // order's current line items, links them, and kicks a detached month-true
  // refresh — so after a reload the row shows the SyncChip until the report
  // lands (the fast-poll streams it in).
  const onAcceptSuggestion = async (id: string) => {
    const s = gamSuggestions[id];
    if (!s?.gamOrderId) return;
    setSavingIds((prev) => new Set(prev).add(id));
    try {
      await acceptGamLink(id, {
        gamOrderId: String(s.gamOrderId),
        gamOrderName: s.gamOrderName,
        matchMethod: s.matchMethod === 'order-number' ? 'order-number' : 'advertiser-date',
      });
      setGamSuggestions((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setLinkDiagnostics((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      flash('success', 'GAM link confirmed', 'Pacing fills in as the month report lands (usually a few minutes).');
      kickGamPolling();
      const month = selectedMonthRef.current;
      await loadInvoices(month);
    } catch (err: any) {
      if (err?.status === 409) {
        // Conflict = the link already changed under us (webhook linked it, a
        // manual link exists, or the order has no linkable lines). The chip is
        // stale either way — drop it and reload so the row reflects reality.
        const reasonText: Record<string, string> = {
          unchanged: 'This row is already linked to that order.',
          'manual-link-preserved': 'A manually-set link exists on this row — it always wins.',
          'no-linkable-line-items': 'That GAM order has no linkable line items yet (all canceled, draft, or disapproved).',
        };
        flash('info', 'Link not applied', reasonText[err?.message] ?? err?.message);
        setGamSuggestions((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        await loadInvoices(selectedMonthRef.current);
      } else {
        flash('error', 'Link failed', err?.message);
      }
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const revertRow = (id: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const discardAll = () => setDrafts({});

  const toggleEditMode = () => {
    if (editMode && dirtyIds.length > 0) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Discard ${dirtyIds.length} unsaved row change(s)?`)) return;
      setDrafts({});
    }
    setEditMode((m) => !m);
  };

  const onMarkReviewed = async (id: string) => {
    try {
      const { invoice } = await markInvoiceReviewed(id);
      mergeInvoice(invoice);
      flash('success', 'Marked reviewed.');
    } catch (err: any) {
      flash('error', 'Could not mark reviewed', err?.message);
    }
  };

  const onResync = async () => {
    setResyncing(true);
    try {
      const { summary } = await resyncPacing({ month: selectedMonth });
      const parts = [];
      if (summary.overDeliveryExceeded) parts.push(`${summary.overDeliveryExceeded} over-delivered`);
      if (summary.overDeliveryApproaching) parts.push(`${summary.overDeliveryApproaching} approaching goal`);
      if (summary.alertsSent) parts.push(`${summary.alertsSent} alert email(s) sent`);
      if (summary.errors.length) parts.push(`${summary.errors.length} row(s) had sync issues`);
      flash(
        summary.overDeliveryExceeded ? 'warning' : 'success',
        `Pacing synced — ${summary.invoicesUpdated} row(s) updated`,
        parts.join(' · ') || undefined,
      );
      await loadInvoices(selectedMonth);
    } catch (err: any) {
      flash('error', 'Resync failed', err?.message);
    } finally {
      setResyncing(false);
    }
  };

  const onAutoLink = async () => {
    setAutoLinking(true);
    try {
      const { summary } = await autoLinkGam({ month: selectedMonth });
      setLinkDiagnostics(buildLinkDiagnostics(summary));
      setGamSuggestions(buildSuggestionMap(summary));
      flash(
        'info',
        'GAM auto-link finished',
        `${summary.applied} linked · ${summary.suggested} to review · ${summary.unmatched} unmatched`,
      );
      await loadInvoices(selectedMonth);
    } catch (err: any) {
      flash('error', 'Auto-link failed', err?.message);
    } finally {
      setAutoLinking(false);
    }
  };

  const onBackfill = async () => {
    setBackfilling(true);
    try {
      const { summary } = await backfillIo({ month: selectedMonth });
      flash(
        summary.errors.length ? 'warning' : 'info',
        `IO backfill — ${summary.invoicesEnriched} invoice(s) enriched`,
        `${summary.fieldsFilled} field(s) filled` +
          (summary.errors.length ? ` · ${summary.errors.length} error(s)` : ''),
      );
      await loadInvoices(selectedMonth);
    } catch (err: any) {
      flash('error', 'IO backfill failed', err?.message);
    } finally {
      setBackfilling(false);
    }
  };

  // Find (dry-run) duplicate invoice rows for the selected month and, if the
  // accountant confirms, merge the safe ones. Preview is available to managers;
  // the merge (which deletes redundant docs, snapshotted first) needs canBill.
  const onFindDuplicates = async () => {
    setDeduping(true);
    try {
      const { result } = await dedupeInvoices({ month: selectedMonth, apply: false });
      if (result.groupsFound === 0) {
        flash('success', 'No duplicates found', `Scanned ${result.invoicesScanned} row(s) in ${monthLabel(selectedMonth)}.`);
        return;
      }
      const mergeable = result.groupsFound - result.groupsSkipped;
      const summaryMsg =
        `${result.groupsFound} duplicate group(s)` +
        (result.groupsSkipped ? ` · ${result.groupsSkipped} need manual review (conflicting $)` : '');
      if (!status.canBill) {
        flash('warning', 'Duplicates found', `${summaryMsg}. Ask an accountant to merge them.`);
        return;
      }
      if (mergeable <= 0) {
        flash('warning', 'Duplicates found', `${summaryMsg}. None can be auto-merged — reconcile manually.`);
        return;
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Merge ${mergeable} duplicate group(s) in ${monthLabel(selectedMonth)}?\n\n` +
          `The redundant row in each group is snapshotted (recoverable) and deleted; ` +
          `the kept row is filled in from it. ` +
          `${result.groupsSkipped ? `${result.groupsSkipped} group(s) with conflicting amounts are left untouched.` : ''}`,
      )) {
        return;
      }
      const applied = await dedupeInvoices({ month: selectedMonth, apply: true });
      flash(
        'success',
        `Merged ${applied.result.groupsMerged} duplicate group(s)`,
        `${applied.result.docsDeleted} redundant row(s) removed` +
          (applied.result.groupsSkipped ? ` · ${applied.result.groupsSkipped} left for manual review` : ''),
      );
      await loadInvoices(selectedMonth);
    } catch (err: any) {
      flash('error', 'Duplicate scan failed', err?.message);
    } finally {
      setDeduping(false);
    }
  };

  const onGenerateInvoice = async (invoiceId: string) => {
    setGeneratingKey(invoiceId);
    try {
      const { generated } = await generateMonthlyInvoice(invoiceId);
      flash('success', `Invoice ${generated.invoiceNumber} generated`, `${fmtUsd(generated.totalBillable)} — opening the PDF in Drive.`);
      if (generated.webViewLink) window.open(generated.webViewLink, '_blank', 'noopener');
      await loadInvoices(selectedMonth);
    } catch (err: any) {
      flash('error', 'Could not generate invoice', err?.message);
    } finally {
      setGeneratingKey(null);
    }
  };

  const onDownloadInvoice = async (invoiceId: string) => {
    try {
      await downloadMonthlyInvoice(invoiceId);
    } catch (err: any) {
      flash('error', 'Download failed', err?.message);
    }
  };

  const onRefreshDelivered = async (invoiceId: string) => {
    setRefreshingDelivered((s) => new Set(s).add(invoiceId));
    const clear = () =>
      setRefreshingDelivered((s) => {
        const n = new Set(s);
        n.delete(invoiceId);
        return n;
      });
    try {
      await refreshMonthlyDelivered(invoiceId);
      flash('info', 'Refreshing delivered impressions', 'Pulling this month’s figure from GAM in the background — it appears on the row in a minute or two.');
    } catch (err: any) {
      flash('error', 'Could not start delivered refresh', err?.message);
      clear();
      return;
    }
    // The report runs detached on the server; the GAM fast-poll loop reloads the
    // displayed month every ~20s until the row's figure lands (the idle refresher
    // only ticks the CURRENT month, but billing is usually for a past month).
    kickGamPolling();
    // Reload soon so the row flips to "Syncing GAM…" (server in-flight flag).
    const month = selectedMonthRef.current;
    window.setTimeout(() => {
      if (selectedMonthRef.current === month) loadInvoices(month);
    }, 2000);
    window.setTimeout(clear, 90000);
  };

  const controlRow = (
    <div style={styles.controlRow}>
      <select
        value={selectedMonth}
        onChange={(e) => {
          const m = e.target.value;
          setExpandedId(null);
          // Stale by month — the fresh auto-link below repopulates both.
          setLinkDiagnostics({});
          setGamSuggestions({});
          loadInvoices(m); // instant cached render
          autoSyncMonth(m); // then link + pace the newly-selected month
        }}
        style={styles.select}
        aria-label="Billing month"
      >
        {monthOptions.map((m) => (
          <option key={m} value={m}>
            {monthLabel(m)}
            {m === currentMonthKey() ? ' (current)' : ''}
          </option>
        ))}
      </select>

      <input
        type="search"
        placeholder="Search advertiser, order, agency…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={styles.search}
        aria-label="Search invoices"
      />

      <FilterChipGroup options={FILTERS} value={filter} onChange={setFilter} />

      <div style={styles.controlSpacer} />

      {status.canManage && (autoLinking || resyncing) && (
        <span style={styles.syncChip} title="Auto-linking GAM orders and refreshing delivery for the selected month">
          <LoadingDots size={5} tone="neutral" />
          Syncing GAM…
        </span>
      )}
      {!loading && invoices.length > 0 && (
        <span style={styles.freshness}>
          {filtered.length === invoices.length
            ? `${invoices.length} row${invoices.length === 1 ? '' : 's'}`
            : `${filtered.length} of ${invoices.length} rows`}
        </span>
      )}
      {lastFetchedAt && (
        <span style={styles.freshness}>
          Updated {new Date(lastFetchedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </span>
      )}
      {status.canManage && (
        <>
          <button className="inv-btn" style={styles.ghostBtn} onClick={onAutoLink} disabled={autoLinking} title="Match invoices to GAM orders and refresh delivery">
            {autoLinking ? 'Linking…' : 'Auto-link GAM'}
          </button>
          <button className="inv-btn" style={styles.ghostBtn} onClick={onResync} disabled={resyncing}>
            {resyncing ? 'Syncing…' : 'Resync pacing'}
          </button>
          <button className="inv-btn" style={styles.ghostBtn} onClick={onBackfill} disabled={backfilling} title="Read IO PDFs from Drive to fill missing contracted impressions / dates">
            {backfilling ? 'Reading IOs…' : 'Backfill from IO'}
          </button>
          <button className="inv-btn" style={styles.ghostBtn} onClick={onFindDuplicates} disabled={deduping} title="Find duplicate rows for this month (same campaign under both its O-… and CP… id) and merge the safe ones">
            {deduping ? 'Scanning…' : 'Find duplicates'}
          </button>
          <button className="inv-btn" style={styles.ghostBtn} onClick={() => setImportOpen(true)}>
            Import from Sheet
          </button>
          <button
            className="inv-btn"
            style={editMode ? styles.primaryBtnActive : styles.primaryBtn}
            onClick={toggleEditMode}
          >
            {editMode ? 'Done editing' : 'Edit mode'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <DashboardFrame
      eyebrow="Invoicing · Monthly tracker"
      title="Invoicing Tracker"
      controlRow={controlRow}
    >
      {toast && (
        <div style={styles.toastWrap}>
          <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}

      <section style={styles.kpiRow}>
        <KpiFilterButton active={filter === 'active'} onClick={() => setFilter(filter === 'active' ? 'all' : 'active')} label="Show active campaigns">
          <KpiCard label="Active campaigns" value={loading ? '—' : kpis.active} live={kpis.active > 0} />
        </KpiFilterButton>
        <KpiCard label="Total planned" value={loading ? '—' : fmtUsd(kpis.planned)} />
        <KpiCard label="Total billable" value={loading ? '—' : fmtUsd(kpis.billable)} />
        <KpiFilterButton active={filter === 'needs_review'} onClick={() => setFilter(filter === 'needs_review' ? 'all' : 'needs_review')} label="Show invoices that need review">
          <KpiCard
            label="Needs review"
            value={loading ? '—' : kpis.needsReview}
            up={kpis.needsReview > 0 ? false : null}
          />
        </KpiFilterButton>
        <KpiFilterButton active={filter === 'over_risk'} onClick={() => setFilter(filter === 'over_risk' ? 'all' : 'over_risk')} label="Show over-delivery risks">
          <KpiCard
            label="Over-delivery risk"
            value={loading ? '—' : kpis.overRisk}
            up={kpis.overRisk > 0 ? false : null}
            delta={kpis.overRisk > 0 ? 'Action needed' : undefined}
          />
        </KpiFilterButton>
      </section>

      {editMode && (
        <div style={styles.editBanner}>
          ✎ Edit mode — expand a row to override any field. {!status.canBill && 'Billable Total & Status are locked (accountant only).'}
        </div>
      )}

      {error && (
        <div style={styles.error}>
          <span>{error}</span>
          <button className="inv-btn" style={styles.retryBtn} onClick={() => loadInvoices(selectedMonth)}>
            Retry
          </button>
        </div>
      )}

      {loading && invoices.length === 0 && !error && (
        <div style={styles.loadingWrap} role="status" aria-label="Loading invoices">
          <LoadingDots />
          <span style={styles.loadingText}>Loading {monthLabel(selectedMonth)}…</span>
        </div>
      )}

      {!loading && filtered.length === 0 && !error && (
        invoices.length === 0 ? (
          <EmptyState
            brand={false}
            icon="🧾"
            title={`No invoices for ${monthLabel(selectedMonth)}`}
            subtitle={
              status.canManage
                ? 'Import the historical tracker sheets to backfill this month, or create rows manually — GAM auto-link and pacing take over from there.'
                : 'Invoices appear here once the Ad Ops team imports or creates them.'
            }
            cta={
              status.canManage ? (
                <button className="inv-btn" style={styles.emptyAction} onClick={() => setImportOpen(true)}>
                  Import from Sheet
                </button>
              ) : undefined
            }
          />
        ) : (
          <EmptyState
            brand={false}
            icon="🔍"
            title="No matches"
            subtitle={`None of the ${invoices.length} invoice(s) in ${monthLabel(selectedMonth)} match the current search or filter.`}
            cta={
              <button
                className="inv-btn"
                style={styles.emptyAction}
                onClick={() => {
                  setFilter('all');
                  setSearch('');
                }}
              >
                Clear filters
              </button>
            }
          />
        )
      )}

      {filtered.length > 0 && (
        <div className="inv-table-wrap" style={styles.tableWrap}>
          <table className="inv-table" style={styles.table}>
            <thead>
              <tr>
                {COLUMNS.map((c, i) => (
                  <th
                    key={i}
                    className={i === 0 ? 'inv-sticky-1' : i === 1 ? 'inv-sticky-2' : undefined}
                    style={c.align === 'right' ? styles.thRight : styles.th}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaignGroups.map((group) => (
                <React.Fragment key={group.campaignKey}>
                  <tr style={styles.groupRow}>
                    <td colSpan={COL_COUNT} className="inv-group-cell" style={styles.groupCell}>
                      <div style={styles.groupInner}>
                        <div className="inv-group-meta" style={styles.groupMeta}>
                          <span style={styles.groupAdv}>{group.advertiser}</span>
                          {group.orderId && <span style={styles.groupChip}>{group.orderId}</span>}
                          <span style={styles.groupCount}>
                            {group.totalMonths} month{group.totalMonths === 1 ? '' : 's'}
                            {group.spansOtherMonths ? ' · full campaign' : ''}
                          </span>
                          {group.allConfirmed ? (
                            <span style={styles.confirmedBadge}>ALL CONFIRMED</span>
                          ) : (
                            <span style={styles.pendingBadge} title="At least one month isn't CONFIRMED yet — invoices can't be generated until the accountant confirms delivered = contracted">
                              PENDING CONFIRM
                            </span>
                          )}
                          {group.hasUnpriced && (
                            <span style={styles.unpricedBadge} title="A CONFIRMED month has no Billable Total — it would under-bill">
                              UNPRICED MONTH
                            </span>
                          )}
                        </div>
                        <div className="inv-group-actions" style={styles.groupActions}>
                          <span style={styles.groupTotal} title="Whole-campaign billable total (all months). Invoices are generated per month on each row below.">
                            {fmtUsd(group.totalBillable)}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {group.items.map((inv) => (
                    <InvoiceRow
                      key={inv.id}
                      invoice={inv}
                      expanded={expandedId === inv.id}
                      onToggle={() => setExpandedId((id) => (id === inv.id ? null : inv.id))}
                      editMode={editMode}
                      canBill={status.canBill}
                      canManage={status.canManage}
                      onQuickPatch={(patch) => onQuickPatch(inv.id, patch)}
                      draft={drafts[inv.id] ?? {}}
                      isDirty={!!drafts[inv.id] && Object.keys(drafts[inv.id]).length > 0}
                      saving={savingIds.has(inv.id)}
                      onDraftChange={(field, value) => onDraftChange(inv.id, field, value)}
                      onSaveRow={() => saveRow(inv.id)}
                      onRevertRow={() => revertRow(inv.id)}
                      onMarkReviewed={() => onMarkReviewed(inv.id)}
                      colCount={COL_COUNT}
                      diagnostic={linkDiagnostics[inv.id]}
                      suggestion={gamSuggestions[inv.id]}
                      onAcceptSuggestion={() => onAcceptSuggestion(inv.id)}
                      generating={generatingKey === inv.id}
                      onGenerate={() => onGenerateInvoice(inv.id)}
                      onDownload={() => onDownloadInvoice(inv.id)}
                      onRefreshDelivered={() => onRefreshDelivered(inv.id)}
                      refreshingDelivered={refreshingDelivered.has(inv.id)}
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dirtyIds.length > 0 && (
        <div style={styles.saveBar}>
          <span style={styles.saveBarText}>{dirtyIds.length} row(s) changed</span>
          <div style={styles.saveBarActions}>
            <button className="inv-btn" style={styles.revertBtn} onClick={discardAll}>
              Discard all
            </button>
            <button className="inv-btn" style={styles.primaryBtn} onClick={saveAll}>
              Save all
            </button>
          </div>
        </div>
      )}

      <ImportSheetModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadInvoices(selectedMonth)}
      />
    </DashboardFrame>
  );
};

/** A KPI card that doubles as a filter toggle (keyboard accessible). */
const KpiFilterButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}> = ({ active, onClick, label, children }) => (
  <button
    type="button"
    className="inv-btn"
    onClick={onClick}
    aria-pressed={active}
    aria-label={label}
    title={active ? 'Clear this filter' : label}
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      margin: 0,
      font: 'inherit',
      textAlign: 'inherit',
      display: 'block',
      cursor: 'pointer',
      borderRadius: 'var(--radius-lg)',
      ...(active ? { outline: '2px solid var(--accent)', outlineOffset: 2 } : {}),
      minWidth: 0,
    }}
  >
    {children}
  </button>
);

const ghostBtnBase: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const styles: Record<string, React.CSSProperties> = {
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    padding: '12px 32px',
  },
  select: {
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    cursor: 'pointer',
  },
  search: {
    padding: '8px 12px',
    fontSize: 13,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    minWidth: 240,
  },
  controlSpacer: { flex: 1 },
  freshness: { fontSize: 12, color: 'var(--fg-4)', fontVariantNumeric: 'tabular-nums' },
  syncChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--fg-3)',
    padding: '4px 10px',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--bg-surface-1)',
  },
  ghostBtn: { ...ghostBtnBase, background: 'transparent', border: '1px solid var(--border-1)', color: 'var(--fg-2)' },
  primaryBtn: { ...ghostBtnBase, background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' },
  primaryBtnActive: { ...ghostBtnBase, background: 'var(--fg-1)', border: '1px solid var(--fg-1)', color: '#fff' },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
  },
  editBanner: {
    padding: '10px 14px',
    background: 'var(--accent-soft)',
    color: 'var(--accent)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    fontWeight: 600,
  },
  // Floating toast: doesn't shift the layout and stays visible while scrolled.
  toastWrap: { position: 'fixed', top: 84, right: 24, zIndex: 1000 },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '48px 12px',
  },
  loadingText: { fontSize: 13.5, color: 'var(--fg-3)', fontWeight: 500 },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  retryBtn: {
    ...ghostBtnBase,
    padding: '5px 12px',
    fontSize: 12.5,
    background: 'transparent',
    border: '1px solid var(--danger)',
    color: 'var(--danger)',
    flexShrink: 0,
  },
  emptyAction: { ...ghostBtnBase, background: 'transparent', border: '1px solid var(--border-1)', color: 'var(--fg-2)' },
  // Scrolling/sticky behavior lives in Invoicing.css (.inv-table-wrap). The
  // table needs border-collapse: separate — collapsed borders detach from
  // sticky cells in Chrome/Safari — so row borders sit on the tds (CSS).
  tableWrap: {
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
  },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, fontFamily: 'var(--font-sans)' },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: 'var(--bg-surface-2)',
    borderBottom: '1px solid var(--border-1)',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
    whiteSpace: 'nowrap',
  },
  thRight: {
    textAlign: 'right',
    padding: '10px 12px',
    background: 'var(--bg-surface-2)',
    borderBottom: '1px solid var(--border-1)',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
    whiteSpace: 'nowrap',
  },
  saveBar: {
    position: 'sticky',
    bottom: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 18px',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
  },
  saveBarText: { fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' },
  saveBarActions: { display: 'flex', gap: 10 },
  revertBtn: { ...ghostBtnBase, background: 'transparent', border: '1px solid var(--border-1)', color: 'var(--fg-2)' },

  // ----- campaign group header row -----
  groupRow: { background: 'var(--bg-surface-2)' },
  groupCell: { padding: '8px 12px', borderTop: '2px solid var(--border-2)', borderBottom: '1px solid var(--border-1)' },
  groupInner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  groupMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  groupAdv: { fontWeight: 700, fontSize: 13, color: 'var(--fg-1)' },
  groupChip: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--fg-3)',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    padding: '1px 7px',
    fontVariantNumeric: 'tabular-nums',
  },
  groupCount: { fontSize: 12, color: 'var(--fg-4)' },
  confirmedBadge: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.05em',
    color: 'var(--cg-forest-600)',
    background: 'var(--success-soft)',
    padding: '2px 6px',
    borderRadius: 4,
  },
  pendingBadge: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.05em',
    color: 'var(--warning)',
    background: 'var(--warning-soft)',
    border: '1px solid var(--warning)',
    padding: '2px 6px',
    borderRadius: 4,
  },
  unpricedBadge: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.05em',
    color: 'var(--danger)',
    background: 'var(--danger-soft)',
    border: '1px solid var(--danger)',
    padding: '2px 6px',
    borderRadius: 4,
  },
  groupActions: { display: 'flex', alignItems: 'center', gap: 10 },
  groupTotal: { fontWeight: 700, fontSize: 13, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' },
};

export default InvoicingDashboard;
